import { memo } from 'react';
import { Loader, Text, Box } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { LoadingStateProps } from '../../../types/loading';
import classes from './LoadingState.module.css';

/**
 * Unified Loading State Component
 *
 * Provides consistent loading states across the application with:
 * - Multiple display modes (spinner, skeleton, overlay)
 * - Mantine 8.x theme integration
 * - Accessibility support
 * - Configurable sizing and positioning
 * - Optional loading text display
 */
export const LoadingState = memo<LoadingStateProps>(function LoadingState({
  mode = 'spinner',
  size = 'md',
  text,
  color,
  centered = false,
  fullScreen = false,
  className,
  'aria-label': ariaLabel,
  ...props
}) {
  const { t } = useTranslation();

  // Determine the container class names
  const containerClass = [
    classes.container,
    centered && classes.centered,
    fullScreen && classes.fullScreen,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Determine ARIA label
  const loadingLabel = ariaLabel || t('common.loading');

  // For overlay mode, wrap in overlay container
  if (mode === 'overlay') {
    return (
      <div className={`${classes.overlayContainer} ${fullScreen ? classes.fullScreen : ''}`}>
        <div className={classes.overlayContent}>
          <Loader
            size={size}
            color={color}
            aria-label={loadingLabel}
            {...props}
          />
          {text && (
            <Text className={classes.text} size="sm">
              {text}
            </Text>
          )}
        </div>
      </div>
    );
  }

  // For skeleton mode, return placeholder (will be handled by LoadingSkeleton)
  if (mode === 'skeleton') {
    return (
      <Box className={containerClass} role="progressbar" aria-label={loadingLabel}>
        <Text className={classes.skeletonPlaceholder}>
          {text || t('common.loading')}
        </Text>
      </Box>
    );
  }

  // Default spinner mode
  return (
    <div className={containerClass} role="progressbar" aria-label={loadingLabel}>
      <Loader
        size={size}
        color={color}
        {...props}
      />
      {text && (
        <Text className={classes.text} size="sm">
          {text}
        </Text>
      )}
    </div>
  );
});

LoadingState.displayName = 'LoadingState';

// Export props interface for convenience
export type { LoadingStateProps };